import { QueryRow } from './types'
import { useIsMobile } from '@duck/ui'
import { useTar } from '@/modules/i18n'
import { TableCellsIcon } from '@heroicons/react/24/outline'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useRef } from 'react'

interface QueryResultsDataProps {
  queryResults: QueryRow[] | null;
  tableMode?: boolean;
  isVisible?: boolean;
}

const mobileClasses = 'max-w-[100dvw]'
const ROW_HEIGHT = 48 // Estimated row height in pixels

export const QueryResultsTable = ({
  queryResults,
  tableMode = false,
  isVisible = true,
}: QueryResultsDataProps) => {
  const isMobile = useIsMobile()
  const { t } = useTar()
  const parentRef = useRef<HTMLDivElement>(null)

  const virtualizer = useVirtualizer({
    count: queryResults?.length || 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: isVisible ? 8 : 0,
  })

  if (!queryResults || queryResults.length === 0) {
    const desktopClasses = `h-full `

    return (
      <div
        className={`flex items-center justify-center ${isMobile ? mobileClasses : desktopClasses}`}
      >
        <div className="flex flex-col items-center justify-center space-y-3 text-center px-4">
          <TableCellsIcon className="w-12 h-12 text-muted-foreground/50" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">
              {t('challenges.noQueryResults')}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('challenges.noQueryResultsDescription')}
            </p>
          </div>
        </div>
      </div>
    )
  }
  const keys = Object.keys(queryResults[0])
  const desktopClasses = `h-full min-h-0`

  if (tableMode) {
    return (
      <div
        className={`bg-card overflow-auto custom-scrollbar ${isMobile ? mobileClasses : desktopClasses}`}
      >
        <table className="w-full text-sm border-collapse table-fixed">
          <colgroup>
            {keys.map((key, idx) => (
              <col key={`${key}-${idx}`} className="min-w-[120px]" />
            ))}
          </colgroup>
          <thead className="sticky top-0 z-10 bg-card border-b border-border">
            <tr className="">
              {keys.map((key) => (
                <th
                  key={key}
                  className="px-4 py-2 text-left text-foreground font-medium"
                >
                  {key}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {queryResults.map((row, i) => (
              <tr
                key={i}
                className={`border-b border-border ${i % 2 === 0 ? 'bg-card' : 'bg-card/50'
                  }`}
              >
                {keys.map((key) => (
                  <td key={key} className="px-4 py-2 text-foreground break-words">
                    {String(row[key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <div
      ref={parentRef}
      className={`bg-card overflow-auto custom-scrollbar min-h-0 ${isMobile ? mobileClasses : desktopClasses}`}
    >
      <div className="text-sm min-w-[600px]">
        <div
          className="sticky top-0 bg-card border-b border-border z-[5] grid"
          style={{ gridTemplateColumns: `repeat(${keys.length}, minmax(0, 1fr))` }}
        >
          {keys.map((key) => (
            <div
              key={key}
              className="px-4 py-2 text-start text-foreground font-medium break-words"
            >
              {key}
            </div>
          ))}
        </div>
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {isVisible
            ? virtualizer.getVirtualItems().map((virtualRow) => {
              const row = queryResults[virtualRow.index]
              return (
                <div
                  key={virtualRow.key}
                  className={`absolute top-0 start-0 w-full grid border-b border-border overflow-hidden ${virtualRow.index % 2 === 0 ? 'bg-card' : 'bg-card/50'
                    }`}
                  style={{
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                    gridTemplateColumns: `repeat(${keys.length}, minmax(0, 1fr))`,
                  }}
                >
                  {keys.map((key) => (
                    <div key={key} className="px-4 py-2 text-foreground break-words">
                      {String(row[key])}
                    </div>
                  ))}
                </div>
              )
            })
            : null}
        </div>
      </div>
    </div>
  )
}
